import { useQuery } from "@rocicorp/zero/react"
import { createFileRoute, Link, redirect } from "@tanstack/react-router"
import * as React from "react"
import * as z from "zod/mini"

import type { Row } from "../../generated/schema.ts"
import * as queries from "../../shared/queries.ts"
import { Message } from "../components/Message.tsx"
import { MessageEditor } from "../components/MessageEditor.tsx"
import { useThreadAccounts } from "../hooks/useThreadAccounts.ts"
import { useTimeAgo } from "../hooks/useTimeAgo.ts"
import * as Auth from "../lib/auth.ts"
import { Layout } from "./-components/Layout.tsx"

export const Route = createFileRoute("/$owner/$repo/~/threads")({
  async beforeLoad(opts) {
    const { context, params } = opts
    const authData = await context.queryClient.ensureQueryData({
      ...Auth.queryOptions,
      initialData: context.authData,
    })
    if (!authData)
      throw redirect({
        to: "/$owner/$repo",
        params: { owner: params.owner, repo: params.repo },
      })
  },
  component: RouteComponent,
  head(ctx) {
    const { owner, repo } = ctx.params
    return {
      meta: [{ title: `Threads - ${owner}/${repo}` }],
    }
  },
  loader(opts) {
    const { context } = opts
    if (context.authData?.id)
      context.zero.preload(
        queries.threadsWithSubscriptions(context.authData, {
          repositoryId: context.repository.id,
        }),
      )
  },
  validateSearch: z.object({
    max: z.optional(z.boolean()),
    messageId: z.optional(z.union([z.string(), z.undefined()])),
    threadId: z.optional(z.union([z.string(), z.undefined()])),
  }),
})

function RouteComponent() {
  const context = Route.useRouteContext()
  const search = Route.useSearch()
  const params = Route.useParams()

  const { data: authData } = Auth.useAuthData()
  const [threads] = useQuery(
    queries.threadsWithSubscriptions(authData, {
      repositoryId: context.repository.id,
    }),
    { enabled: Boolean(authData?.id), ttl: "5m" },
  )

  const [repository] = useQuery(
    queries.repositoryWithOwner(authData, {
      repositoryId: context.repositoryId,
    }),
    { ttl: "5m" },
  )

  const repositoryOwner = React.useMemo(() => {
    if (!repository) return context.repository.owner
    if (repository.account)
      return { ...repository.account, type: "account" } as const
    if (repository.organization)
      return { ...repository.organization, type: "organization" } as const
    return context.repository.owner
  }, [context.repository.owner, repository])

  // TODO: Virtual infinite scroll
  return (
    <Layout.Content hide={search.max}>
      <div className="flex h-12 items-center justify-between border-neutral-6 border-b bg-default px-3.5">
        <div className="flex items-center gap-3.5">
          <Link
            className="hidden text-muted hover:text-default max-md:block"
            params={params}
            search={(x) => ({ ...x, nav: x.nav ? undefined : true })}
            to="/$owner/$repo/~/threads"
          >
            <OcticonSidebarExpand16 className="size-4 rotate-180" />
          </Link>

          <div className="flex items-center gap-1 font-medium text-[14px]">
            Threads
          </div>
        </div>
      </div>

      <div className="scrollbar-neutral-6 dark:scrollbar-neutral-7 scrollbar-width-thin scrollbar-gutter-stable flex-1 space-y-5.5 overflow-scroll bg-inset pt-4 pb-0">
        {threads.map((thread) => (
          <div className="px-3.5" key={thread.id}>
            <ThreadRow
              channel={thread.message?.channel}
              message={thread.message}
              messages={thread.messages}
              repositoryId={context.repositoryId}
              repositoryOwner={repositoryOwner}
              thread={thread}
            />
          </div>
        ))}
      </div>
    </Layout.Content>
  )
}

function ThreadRow(props: {
  channel: Pick<Row.channel, "name" | "private"> | undefined
  thread: Pick<
    Row.thread,
    "account_count" | "id" | "last_message_at" | "message_count"
  >
  message:
    | (Pick<
        Row.message,
        "channel_id" | "content" | "deleted_at" | "id" | "inserted_at"
      > & {
        account:
          | Pick<Row.account, "avatar_url" | "last_seen_at" | "login" | "name">
          | undefined
      })
    | undefined
  messages:
    | readonly (Pick<
        Row.message,
        "channel_id" | "content" | "deleted_at" | "id" | "inserted_at"
      > & {
        account:
          | Pick<Row.account, "avatar_url" | "last_seen_at" | "login" | "name">
          | undefined
      })[]
    | undefined
  repositoryId: string
  repositoryOwner: Pick<Row.account | Row.organization, "id" | "login">
}) {
  const {
    channel,
    thread,
    message,
    messages = [],
    repositoryId,
    repositoryOwner,
  } = props

  const maxAvatars = 5
  const overflowAccountCount = thread.account_count - maxAvatars
  const threadAccounts = useThreadAccounts({ thread })
  const accounts = React.useMemo(
    () => threadAccounts.slice(0, maxAvatars),
    [threadAccounts],
  )

  const overflowMessagesCount = thread.message_count - messages.length

  const timeAgo = useTimeAgo(thread.last_message_at)

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between px-1 pe-1.5">
        <div className="flex items-center gap-2">
          {channel && (
            <div className="flex items-center gap-0.5 font-medium text-[13px] text-neutral-11 leading-none">
              {channel?.private ? (
                <OcticonLock16 className="mt-px size-3" />
              ) : (
                <OcticonHash16 className="mt-px size-3" />
              )}
              {channel.name}
            </div>
          )}
          <div className="font-medium text-[13px] text-neutral-10 leading-none dark:text-neutral-9">
            Last reply {timeAgo}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="font-medium text-[13px] text-neutral-10 leading-none dark:text-neutral-9">
            {thread.message_count}{" "}
            {thread.message_count === 1 ? "reply" : "replies"}
          </div>

          <div className="flex gap-0.5">
            {accounts.map((account, i) => (
              <div
                className="relative size-4 overflow-hidden rounded-[3px]"
                key={account.id}
              >
                <img alt={account.login} src={account.avatar_url} />
                {overflowAccountCount && i === maxAvatars - 1 && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-[14px] text-white leading-none">
                    {overflowAccountCount > 9
                      ? "9+"
                      : `+${overflowAccountCount}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-lg border border-neutral-4 bg-default px-3.5 pt-3 pb-3.5 dark:border-neutral-5">
        <Message
          account={message?.account!}
          edited={false}
          editing={false}
          join={false}
          message={message!}
          onCancelEdit={() => {}}
          onEdit={async () => {}}
          repositoryId={repositoryId}
          repositoryOwner={repositoryOwner}
        />
        {overflowMessagesCount > 0 && (
          <div className="font-medium text-[13px] text-accent leading-none">
            Show {overflowMessagesCount} more{" "}
            {overflowMessagesCount === 1 ? "reply" : "replies"}
          </div>
        )}
        {messages.toReversed().map((message) => (
          <Message
            account={message?.account!}
            edited={false}
            editing={false}
            join={false}
            key={message.id}
            message={message!}
            onCancelEdit={() => {}}
            onEdit={async () => {}}
            repositoryId={repositoryId}
            repositoryOwner={repositoryOwner}
          />
        ))}

        <MessageEditor
          channelId={message?.channel_id!}
          key={thread.id}
          onSubmit={async () => {}}
          placeholder="Reply…"
          repositoryId={repositoryId}
          uploadText={undefined}
        />
      </div>
    </div>
  )
}
